import { useMutation } from '@apollo/client/react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  PortalPurchaseRechargeDocument,
  type PortalPurchaseRechargeMutation,
  type PortalPurchaseRechargeMutationVariables,
} from '@/graphql/generated/graphql'

/**
 * Compra de crédito. Genera la factura (el backend confirma el pago vía evento
 * `factura.pagada`). En éxito muestra toast con el nº de factura y llama
 * `onSuccess` para que la página refresque balance + historial.
 *
 * SEAM: cuando exista el flujo de pago (Facturación/Stripe), enchufarlo aquí en
 * `onSuccess` usando el `id`/`invoiceNumber` devueltos (p. ej. navegar a
 * `/app/billing/:invoiceId` o lanzar checkout).
 */
export function usePurchaseRecharge(options?: {
  onSuccess?: (invoiceId: number) => void
}) {
  const [mutate, { loading, error }] = useMutation<
    PortalPurchaseRechargeMutation,
    PortalPurchaseRechargeMutationVariables
  >(PortalPurchaseRechargeDocument, {
    onError: (err) => {
      Sentry.captureException(err, {
        tags: { operation: 'PortalPurchaseRecharge' },
      })
      toast.error('No se pudo procesar la compra', { description: err.message })
    },
  })

  const purchaseRecharge = async (vars: PortalPurchaseRechargeMutationVariables) => {
    const result = await mutate({ variables: vars })
    const invoice = result.data?.portalPurchaseRecharge
    if (invoice) {
      toast.success('Crédito comprado', {
        description: invoice.invoiceNumber
          ? `Factura #${invoice.invoiceNumber} generada. El balance se actualizará al confirmarse el pago.`
          : 'El balance se actualizará al confirmarse el pago.',
      })
      options?.onSuccess?.(invoice.id)
    }
    return invoice ?? null
  }

  return { purchaseRecharge, loading, error }
}
